// src/screens/sales/executor/components/form/hooks/useForm.ts

import { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  ExecutorInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
  OptionItemInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { executorValidations } from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'

const validations: ValidationRulesInterface[] = executorValidations

export const useForm = (
  data: ExecutorInterface,
  setData: React.Dispatch<React.SetStateAction<ExecutorInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: ExecutorInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [disabled, setDisabled] = useState(true)
  const [motivationList, setMotivationList] = useState<any[]>([])
  const [allStores, setAllStores] = useState<any[]>([])

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const { inputHandler } = useInput(setData)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  const getMotivationList = useCallback(async () => {
    try {
      const result = await salesApi.getMotivationList()
      if (result) setMotivationList(result)
    } catch (error) {}
  }, [salesApi])

  const getStoreList = useCallback(async () => {
    try {
      const result = await salesApi.getStoreList()
      if (result) setAllStores(result?.data || [])
    } catch (error) {}
  }, [salesApi])

  const createMotivationSelectList = useCallback((): OptionItemInterface[] => {
    return motivationList.map((item) => ({
      id: String(item.id),
      label: item.name,
      name: item.name,
      value: String(item.id),
    }))
  }, [motivationList])

  const createStoreSelectList = useCallback((): OptionItemInterface[] => {
    return allStores.map((store) => ({
      id: String(store.id),
      label: store.name,
      name: store.name,
      value: String(store.id),
    }))
  }, [allStores])

  const handleStoreSelect = useCallback(
    (storeId: number) => {
      const currentIds = data.storeIds || []
      if (!currentIds.includes(storeId)) {
        setData((prev) => ({ ...prev, storeIds: [...currentIds, storeId] }))
      }
    },
    [data.storeIds, setData],
  )

  const handleStoreRemove = useCallback(
    (storeId: number) => {
      const currentIds = data.storeIds || []
      setData((prev) => ({ ...prev, storeIds: currentIds.filter((id) => id !== storeId) }))
    },
    [data.storeIds, setData],
  )

  useEffect(() => {
    if (el) {
      setData({
        ...el,
      })
    }
  }, [el, setData])

  useEffect(() => {
    getMotivationList()
    getStoreList()
  }, [getMotivationList, getStoreList])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)

      const {
        id,
        createdAt,
        updatedAt,
        uuid,
        clusterId,
        deleted,
        motivation,
        stores,
        user,
        inviteUuid,
        settings,
        ...rest
      } = data

      const payload = {
        ...rest,
        storeIds: data.storeIds || [],
      }

      if (edit && data.id) {
        await salesApi.updateExecutor({ ...payload, id: data.id })
      } else {
        await salesApi.createExecutor(payload)
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
      console.error('Form action error:', error)
    }
  }, [salesApi, data, edit, getList, setModalActive, showCommonLoader, hideCommonLoader])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item as keyof ExecutorInterface])
      }
    }
  }, [data])

  useEffect(() => {
    setDisabled(validationResult.some((el) => el.notValid))
  }, [validationResult])

  return {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    createMotivationSelectList,
    createStoreSelectList,
    handleStoreSelect,
    handleStoreRemove,
    storeValue: data.storeIds || [],
  }
}
